import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSuspenseQuery } from '@tanstack/react-query';
import { SamdAppRepository } from '@samd/api/app_repository';
import {
  BREAST_CANCER_VERSIONS,
  PROSTATE_VERSIONS,
  RELEASE_DATE,
  SAMD_PLATFORM_VERSIONS,
} from '@samd/features/help/build_versions';
import { getSamdWorkflowDefinition } from '@samd/workflows';
import Ce2797Icon from 'src/assets/ce_2797.svg';
import ContactSupportIcon from 'src/assets/contact_support.svg';
import EuRepIcon from 'src/assets/eu_rep.svg';
import FactoryIcon from 'src/assets/factory.svg';
import FactoryBlackedIcon from 'src/assets/factory_blacked.svg';
import FactoryUsIcon from 'src/assets/factory_us.svg';
import ImportIcon from 'src/assets/import.svg';
import InfoBookIcon from 'src/assets/info_book.svg';
import IVDIcon from 'src/assets/ivd.svg';
import ProductInformationIcon from 'src/assets/product_information.svg';
import UdiIcon from 'src/assets/udi.svg';
import UKCAIcon from 'src/assets/ukca.svg';
import UserManualIcon from 'src/assets/user_manual.svg';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Caution } from 'src/components/icons/caution';
import { getApp } from 'src/config/environment';
import { HelpMenu } from 'src/distribution_platforms/features/components/help';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { BuildVersionsFieldsFragment } from 'src/types';
import { App } from 'src/types/app';
import { Region } from 'src/types/env';
import type { WorkflowType } from 'src/types/workflows';

/** Instructions for Use PDF URL for a SaMD workflow. Throws if workflow is not SAMD_US or SAMD_UK. */
export const getInstructionsPdfUrl = (workflowType: WorkflowType): string =>
  getSamdWorkflowDefinition(workflowType).instructionsPdfUrl;

const Divider = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const DarkLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.primary,
}));

const BoldDarkLabel = styled(DarkLabel)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const LightLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.text.secondary,
}));

const ValueText = styled('span')(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightLight,
  color: theme.palette.text.secondary,
}));

const CenteredRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const LegalNotice = styled(Box)(({ theme }) => ({
  lineHeight: '120%',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));

const InfoBookIconImg: React.FC = () => (
  <img src={InfoBookIcon} alt='Info Book' style={{ width: 78, height: 54 }} />
);

// Regulatory icons (IVD + Info Book) used across regions
const IVDInfoBookIcons: React.FC = () => (
  <>
    <img src={IVDIcon} alt='IVD' style={{ width: 86, height: 54 }} />
    <InfoBookIconImg />
  </>
);

const buildUkProductInformation = (data: BuildVersionsFieldsFragment) => {
  return (
    <DialogContent sx={{ textAlign: 'center', pt: 2, pb: 0, mx: '-24px' }}>
      <Box>
        <BoldDarkLabel data-testid='platform-version'>
          ArteraAI SaMD Platform:{' '}
          <ValueText>Version {SAMD_PLATFORM_VERSIONS[App.SamdUk]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel data-testid='prostate-version'>
          ArteraAI Prostate Biopsy Assay:{' '}
          <ValueText>Version {PROSTATE_VERSIONS[Region.UK]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel data-testid='breast-version'>
          ArteraAI Breast Cancer Assay:{' '}
          <ValueText>Version {BREAST_CANCER_VERSIONS[Region.UK]}</ValueText>
        </BoldDarkLabel>
      </Box>
      <BoldDarkLabel>Build Numbers:</BoldDarkLabel>
      <Stack direction='column'>
        <LightLabel>
          UI: <ValueText>{data.ui}</ValueText>
        </LightLabel>
        <LightLabel>
          BackEnd: <ValueText>{data.backend}</ValueText>
        </LightLabel>
        <LightLabel>
          AI Executor: <ValueText>{data.aiExecutor}</ValueText>
        </LightLabel>
        <LightLabel>
          Slide Pre-processor: <ValueText>{data.slidePreprocessor}</ValueText>
        </LightLabel>
        <LightLabel>
          Prostate Prognostic Model:{' '}
          <ValueText>{data.prostatePrognosticModel}</ValueText>
        </LightLabel>
        <LightLabel>
          Prostate ST-ADT Model:{' '}
          <ValueText>{data.prostateSTADTModel}</ValueText>
        </LightLabel>
        <LightLabel>
          Breast Prognostic Model:{' '}
          <ValueText>{data.breastPrognosticModel}</ValueText>
        </LightLabel>
      </Stack>
      <CenteredRow sx={{ gap: 2.5, my: 1.25 }}>
        <img
          src={FactoryIcon}
          alt='Factory'
          style={{ width: 54, height: 54 }}
        />
        <DarkLabel data-testid='release-date'>
          Release date: <ValueText>{RELEASE_DATE[Region.UK]}</ValueText>
        </DarkLabel>
      </CenteredRow>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          my: 1.25,
        }}
      >
        <img src={UKCAIcon} alt='UKCA' style={{ width: 54, height: 54 }} />
        <IVDInfoBookIcons />
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          my: 1.25,
        }}
      >
        <CenteredRow sx={{ gap: 2.5 }}>
          <img
            src={FactoryBlackedIcon}
            alt='Factory Blacked'
            style={{ width: 58, height: 54 }}
          />
          <Box
            sx={{
              textAlign: 'left',
              lineHeight: '100%',
            }}
          >
            <DarkLabel sx={{ lineHeight: '120%' }}>
              Manufactured By
              <LightLabel sx={{ display: 'block' }}>Artera Inc.</LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                108 First Street Los Altos,
              </LightLabel>
              <LightLabel sx={{ display: 'block' }}>CA 94022</LightLabel>
            </DarkLabel>
          </Box>
        </CenteredRow>
        <DarkLabel sx={{ lineHeight: '120%' }}>
          UK Responsible Person:
          <LightLabel>Freyr Life Sciences LTD</LightLabel>
          <LightLabel>9 Greyfriars Road Reading Berkshire England,</LightLabel>
          <LightLabel>United Kingdom RG1 1NU</LightLabel>
          <Link href='mailto:ukrp@freyrsolutions.com' color='primary'>
            <LightLabel>ukrp@freyrsolutions.com</LightLabel>
          </Link>
        </DarkLabel>
        <LegalNotice>
          <LightLabel>© Copyright 2026 Artera Inc.</LightLabel>
          <LightLabel>
            ArteraAI Prostate Biopsy Assay and ArteraAI Breast Cancer Assay are
            registered trademarks of Artera® in the United States and/or other
            countries.
          </LightLabel>
        </LegalNotice>
      </Box>
    </DialogContent>
  );
};

const buildEuProductInformation = (data: BuildVersionsFieldsFragment) => {
  return (
    <DialogContent sx={{ textAlign: 'center', pt: 2, pb: 0, mx: '-24px' }}>
      <Box>
        <BoldDarkLabel data-testid='platform-version'>
          ArteraAI SaMD Platform:{' '}
          <ValueText>Version {SAMD_PLATFORM_VERSIONS[App.SamdEu]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel data-testid='prostate-version'>
          ArteraAI Prostate Biopsy Assay:{' '}
          <ValueText>Version {PROSTATE_VERSIONS[Region.EU]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel data-testid='breast-version'>
          ArteraAI Breast Cancer Assay:{' '}
          <ValueText>Version {BREAST_CANCER_VERSIONS[Region.EU]}</ValueText>
        </BoldDarkLabel>
      </Box>
      <BoldDarkLabel>Build Numbers:</BoldDarkLabel>
      <Stack direction='column'>
        <LightLabel>
          UI: <ValueText>{data.ui}</ValueText>
        </LightLabel>
        <LightLabel>
          BackEnd: <ValueText>{data.backend}</ValueText>
        </LightLabel>
        <LightLabel>
          AI Executor: <ValueText>{data.aiExecutor}</ValueText>
        </LightLabel>
        <LightLabel>
          Slide Pre-processor: <ValueText>{data.slidePreprocessor}</ValueText>
        </LightLabel>
        <LightLabel>
          Prostate Prognostic Model:{' '}
          <ValueText>{data.prostatePrognosticModel}</ValueText>
        </LightLabel>
        <LightLabel>
          Prostate ST-ADT Model:{' '}
          <ValueText>{data.prostateSTADTModel}</ValueText>
        </LightLabel>
        <LightLabel>
          Breast Prognostic Model:{' '}
          <ValueText>{data.breastPrognosticModel}</ValueText>
        </LightLabel>
      </Stack>
      <CenteredRow sx={{ gap: 2.5, my: 1.25 }}>
        <img
          src={FactoryIcon}
          alt='Factory'
          style={{ width: 54, height: 54 }}
        />
        <DarkLabel data-testid='release-date'>
          Release date: <ValueText>{RELEASE_DATE[Region.EU]}</ValueText>
        </DarkLabel>
      </CenteredRow>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          my: 1.25,
        }}
      >
        <img src={Ce2797Icon} alt='CE 2797' style={{ width: 86, height: 86 }} />
        <IVDInfoBookIcons />
      </Box>
      <Divider />
      <CenteredRow sx={{ my: 1.25 }}>
        <img
          src={UdiIcon}
          alt='UDI'
          style={{ width: 84, height: 52, marginRight: 16 }}
        />
        <Box
          sx={{
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box>
            <LightLabel sx={{ display: 'block' }}>
              ArteraAI Prostate Biopsy Assay:
            </LightLabel>
            <LightLabel sx={{ display: 'block' }}>
              (01)00850062141001 (8012) 1.x.x
            </LightLabel>
          </Box>
          <Box
            sx={{
              mt: 0.75,
            }}
          >
            <LightLabel sx={{ display: 'block' }}>
              ArteraAI Breast Cancer Assay:
            </LightLabel>
            <LightLabel sx={{ display: 'block' }}>
              (01)00850062141018 (8012) 1.x.x
            </LightLabel>
          </Box>
        </Box>
      </CenteredRow>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          my: 1.25,
        }}
      >
        <CenteredRow sx={{ gap: 2.5 }}>
          <img
            src={FactoryBlackedIcon}
            alt='Factory Blacked'
            style={{ width: 58, height: 54 }}
          />
          <Box
            sx={{
              textAlign: 'left',
              lineHeight: '100%',
            }}
          >
            <DarkLabel sx={{ lineHeight: '120%' }}>
              Manufactured By
              <LightLabel sx={{ display: 'block' }}>Artera Inc.</LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                108 First Street Los Altos,
              </LightLabel>
              <LightLabel sx={{ display: 'block' }}>CA 94022</LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                SRN: US-MF-000044636
              </LightLabel>
            </DarkLabel>
          </Box>
        </CenteredRow>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <img src={EuRepIcon} alt='EU REP' style={{ width: 90 }} />
            <img
              src={ImportIcon}
              alt='Import'
              style={{ width: 50, height: 50 }}
            />
          </Box>
          <DarkLabel sx={{ lineHeight: '120%', display: 'block', mt: 1 }}>
            EU Responsible Person:
          </DarkLabel>
          <LightLabel>Star Poland Sp. z o.o.</LightLabel>
          <LightLabel>Grabarska 1</LightLabel>
          <LightLabel>50-079 Wrocław, Poland</LightLabel>
        </Box>
      </Box>
    </DialogContent>
  );
};

const buildUsProductInformation = (data: BuildVersionsFieldsFragment) => {
  return (
    <DialogContent sx={{ textAlign: 'center', pt: 2, pb: 0, mx: '-24px' }}>
      <Box>
        <BoldDarkLabel data-testid='platform-version'>
          ArteraAI SaMD Platform:{' '}
          <ValueText>Version {SAMD_PLATFORM_VERSIONS[App.SamdUs]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel data-testid='prostate-version'>
          ArteraAI Prostate:{' '}
          <ValueText>Version {PROSTATE_VERSIONS[Region.US]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel data-testid='breast-version'>
          ArteraAI Breast:{' '}
          <ValueText>Version {BREAST_CANCER_VERSIONS[Region.US]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel>Build Numbers:</BoldDarkLabel>
        <Stack direction='column' data-testid='build-versions'>
          <LightLabel>
            UI: <ValueText>{data.ui}</ValueText>
          </LightLabel>
          <LightLabel>
            BackEnd: <ValueText>{data.backend}</ValueText>
          </LightLabel>
          <LightLabel>
            AI Executor: <ValueText>{data.aiExecutor}</ValueText>
          </LightLabel>
          <LightLabel>
            Prostate Prognostic Model:{' '}
            <ValueText>{data.prostatePrognosticModel}</ValueText>
          </LightLabel>
          <LightLabel>
            Slide Pre-processor: <ValueText>{data.slidePreprocessor}</ValueText>
          </LightLabel>
          <LightLabel>
            Breast Prognostic Model:{' '}
            <ValueText>{data.breastPrognosticModel}</ValueText>
          </LightLabel>
        </Stack>
        <CenteredRow sx={{ my: 1.2 }}>
          <img
            src={FactoryUsIcon}
            alt='Factory'
            style={{ width: 60, height: 60, marginRight: 16 }}
          />
          <DarkLabel data-testid='release-date'>
            Release date: <ValueText>{RELEASE_DATE[Region.US]}</ValueText>
          </DarkLabel>
        </CenteredRow>
        <Divider />
        <CenteredRow sx={{ gap: 4, my: 2 }}>
          <IVDInfoBookIcons />
          <BoldDarkLabel
            sx={(theme) => ({ fontSize: theme.typography.h6.fontSize })}
          >
            Rx only
          </BoldDarkLabel>
        </CenteredRow>
        <Divider />
        <CenteredRow sx={{ my: 2 }}>
          <img
            src={FactoryBlackedIcon}
            alt='Factory Blacked'
            style={{ width: 60, height: 60, marginRight: 16 }}
          />
          <Box
            sx={{
              textAlign: 'left',
              lineHeight: '100%',
            }}
          >
            <DarkLabel sx={{ lineHeight: '120%' }}>
              Manufactured By
              <LightLabel sx={{ display: 'block' }}>Artera Inc.</LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                108 First Street Los Altos,
              </LightLabel>
              <LightLabel sx={{ display: 'block' }}>CA 94022</LightLabel>
            </DarkLabel>
          </Box>
        </CenteredRow>
        <CenteredRow sx={{ my: 1 }}>
          <img
            src={UdiIcon}
            alt='UDI'
            style={{ width: 84, height: 52, marginRight: 16 }}
          />
          <Box
            sx={{
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box>
              <LightLabel sx={{ display: 'block' }}>
                ArteraAI Prostate:
              </LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                (01)00850062141025(10) 1.1.x
              </LightLabel>
            </Box>
            <Box
              sx={{
                mt: 0.75,
              }}
            >
              <LightLabel sx={{ display: 'block' }}>
                ArteraAI Breast:
              </LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                (01)00850062141032(10) 1.1.x
              </LightLabel>
            </Box>
          </Box>
        </CenteredRow>
        <LegalNotice>
          <LightLabel>© Copyright 2026 Artera Inc.</LightLabel>
          <LightLabel>
            ArteraAI Prostate and ArteraAI Breast are registered trademarks of
            Artera® in the United States and/or other countries.
          </LightLabel>
        </LegalNotice>
      </Box>
    </DialogContent>
  );
};

const buildDemoProductInformation = (data: BuildVersionsFieldsFragment) => {
  return (
    <DialogContent
      sx={{ textAlign: 'center', pt: 2, pb: 1.2, mx: '-24px', px: 8 }}
    >
      <Box>
        <BoldDarkLabel data-testid='platform-version' sx={{ mb: 1.2 }}>
          ArteraAI Demo Platform:{' '}
          <ValueText>Version {SAMD_PLATFORM_VERSIONS[App.SamdDemo]}</ValueText>
        </BoldDarkLabel>
        <BoldDarkLabel>Build Numbers:</BoldDarkLabel>
        <Stack direction='column' data-testid='build-versions'>
          <LightLabel>
            UI: <ValueText>{data.ui}</ValueText>
          </LightLabel>
          <LightLabel>
            BackEnd: <ValueText>{data.backend}</ValueText>
          </LightLabel>
        </Stack>
        <CenteredRow sx={{ mt: 2.4, mb: 1.2 }}>
          <img
            src={FactoryUsIcon}
            alt='Factory'
            style={{ width: 60, height: 60, marginRight: 16 }}
          />
          <DarkLabel data-testid='release-date'>
            Release date: <ValueText>{RELEASE_DATE[Region.US]}</ValueText>
          </DarkLabel>
        </CenteredRow>
        <CenteredRow sx={{ my: 2 }}>
          <img
            src={FactoryBlackedIcon}
            alt='Factory Blacked'
            style={{ width: 80, height: 80, marginRight: 16 }}
          />
          <Box
            sx={{
              textAlign: 'left',
              lineHeight: '100%',
            }}
          >
            <DarkLabel sx={{ lineHeight: '120%' }}>
              Manufactured By
              <LightLabel sx={{ display: 'block' }}>Artera Inc.</LightLabel>
              <LightLabel sx={{ display: 'block' }}>
                108 First Street Los Altos,
              </LightLabel>
              <LightLabel sx={{ display: 'block' }}>CA 94022</LightLabel>
            </DarkLabel>
          </Box>
        </CenteredRow>
        <LegalNotice>
          <LightLabel>© Copyright 2026 Artera Inc.</LightLabel>
        </LegalNotice>
      </Box>
    </DialogContent>
  );
};

const buildProductInformation = (
  data: BuildVersionsFieldsFragment,
  app: App,
) => {
  switch (app) {
    case App.SamdUs:
      return buildUsProductInformation(data);
    case App.SamdUk:
      return buildUkProductInformation(data);
    case App.SamdDemo:
      return buildDemoProductInformation(data);
    case App.SamdEu:
      return buildEuProductInformation(data);
    default:
      throw new Error(`Unsupported app: ${app}`);
  }
};

export const HelpDropdown: React.FC<{
  id?: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  appRepository: SamdAppRepository;
  loaderData: unknown;
}> = ({ id, open, anchorEl, onClose, appRepository, loaderData }) => {
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [showProductInfoPopup, setShowProductInfoPopup] = useState(false);

  const workflowType = useWorkflowType();
  const workflow = getSamdWorkflowDefinition(workflowType);

  const { data } = useSuspenseQuery({
    queryKey: (loaderData as { queryKey: readonly unknown[] }).queryKey,
    queryFn: () => appRepository.fetchBuildVersions(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const handleContactSupport = useCallback(() => {
    setShowSupportPopup(true);
  }, []);

  const handleProductInfo = useCallback(() => {
    setShowProductInfoPopup(true);
  }, []);

  const items = [
    {
      icon: ContactSupportIcon,
      label: 'Contact Support',
      ariaLabel: 'support',
      onClick: () => {
        handleContactSupport();
        onClose();
      },
    },
    ...(workflow.showUserManualInHelp
      ? [
          {
            icon: UserManualIcon,
            label: 'User Manual',
            ariaLabel: 'manual',
            onClick: () => {
              window.open(
                workflow.instructionsPdfUrl,
                '_blank',
                'noopener,noreferrer',
              );
              onClose();
            },
          },
        ]
      : []),
    {
      icon: ProductInformationIcon,
      label: 'Product Information',
      ariaLabel: 'information',
      onClick: () => {
        handleProductInfo();
        onClose();
      },
    },
  ];

  return (
    <>
      <HelpMenu
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        items={items}
      />

      <FeedbackPopup
        data={{
          primaryButtonText: 'OK',
          secondaryButtonText: '',
          title: 'Contact Support',
          type: FeedbackType.Primary,
          icon: Caution,
        }}
        isDialogOpen={showSupportPopup}
        onPrimaryButtonClick={() => setShowSupportPopup(false)}
        handleDialogClose={() => setShowSupportPopup(false)}
      >
        <Typography> Questions? Concerns? </Typography>
        <Typography>
          Call:{' '}
          <Link href='tel:+16502397018' color='primary' underline='hover'>
            +1 650 239 7018
          </Link>
        </Typography>
        <Typography>
          Email:{' '}
          <Link
            href={`mailto:${workflow.supportEmail}`}
            color='primary'
            underline='hover'
          >
            {workflow.supportEmail}
          </Link>
        </Typography>
        <Typography>
          Visit us at{' '}
          <Link
            href='https://artera.ai'
            color='primary'
            underline='hover'
            target='_blank'
            rel='noopener noreferrer'
          >
            artera.ai
          </Link>
        </Typography>
      </FeedbackPopup>

      <Dialog
        open={showProductInfoPopup}
        onClose={() => setShowProductInfoPopup(false)}
        maxWidth='xs'
        fullWidth={false}
      >
        <DialogTitle>Product Information</DialogTitle>
        {buildProductInformation(data, getApp())}
        <DialogActions>
          <Button
            onClick={() => setShowProductInfoPopup(false)}
            variant='contained'
            size='large'
            sx={{ width: '160px' }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
